import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { HELP_DEFINITIONS } from '../data/helpDefinitions';
import { userKey } from '../utils/userStorage';
import { useAuth } from './AuthContext';
import { preferencesService } from '../services/api';

const STORAGE_KEY = 'nizus_help_prefs';

const DEFAULT_PREFS = {
    globalEnabled: true,
    autoTrigger: true,
    showTriggerButton: true,
    seenPages: {},
};

function loadPrefs() {
    try {
        const raw = localStorage.getItem(userKey(STORAGE_KEY));
        if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return { ...DEFAULT_PREFS };
}

function savePrefs(prefs) {
    try {
        localStorage.setItem(userKey(STORAGE_KEY), JSON.stringify(prefs));
    } catch { /* quota exceeded */ }
}

// Debounced server sync for seenPages
let syncTimer = null;
function syncSeenPagesToServer(seenPages) {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
        preferencesService.save({ helpSeenPages: seenPages }).catch(() => {});
    }, 1000);
}

const HelpContext = createContext();

export const HelpProvider = ({ children }) => {
    const { user } = useAuth();
    const [prefs, setPrefs] = useState(loadPrefs);
    const [tourActive, setTourActive] = useState(false);
    const [currentTourPage, setCurrentTourPage] = useState(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [filteredSteps, setFilteredSteps] = useState([]);
    // True once server-side helpSeenPages have been fetched (or no user / fetch failed).
    // Auto-trigger logic in consumers MUST wait for this to avoid replaying tours
    // on a fresh device while the merge from server is still in flight.
    const [isPrefsSynced, setIsPrefsSynced] = useState(false);

    // Reload prefs when user changes (login/logout) — userKey resolves to a different
    // localStorage key per user, so prefs must be re-read after authentication changes.
    // Also merge server-side seenPages to ensure cross-device consistency.
    useEffect(() => {
        const localPrefs = loadPrefs();
        setPrefs(localPrefs);
        setIsPrefsSynced(false);

        if (user?.id) {
            preferencesService.get().then(serverPrefs => {
                const serverSeen = serverPrefs?.helpSeenPages;
                if (serverSeen && typeof serverSeen === 'object') {
                    setPrefs(prev => {
                        const merged = { ...prev, seenPages: { ...serverSeen, ...prev.seenPages } };
                        savePrefs(merged);
                        // Push any local-only pages back to server
                        const localHasMore = Object.keys(prev.seenPages).some(k => !serverSeen[k]);
                        if (localHasMore) {
                            syncSeenPagesToServer(merged.seenPages);
                        }
                        return merged;
                    });
                } else if (Object.keys(localPrefs.seenPages || {}).length > 0) {
                    // Server has nothing yet (new user, or pre-fix accounts whose
                    // helpSeenPages were silently dropped by the backend whitelist).
                    // Push up the local state so the next device gets the merged view.
                    syncSeenPagesToServer(localPrefs.seenPages);
                }
            }).catch(() => {}).finally(() => {
                setIsPrefsSynced(true);
            });
        } else {
            // No user → no server sync needed, prefs are "synced" (local only).
            setIsPrefsSynced(true);
        }
    }, [user?.id]);

    // Active help page: components can declare which tour applies to the current view
    // (e.g., LevelOverview sets 'stats-tab' when on stats tab, 'level-overview' otherwise)
    const [activeHelpPage, setActiveHelpPage] = useState(null);

    // Overlay registration: overlays (exam, quiz, course) register their help pageId
    // so that background page tours are blocked while an overlay is active
    const activeOverlaysRef = useRef(new Set());
    const [activeOverlaysCount, setActiveOverlaysCount] = useState(0);

    const registerOverlay = useCallback((pageId) => {
        activeOverlaysRef.current.add(pageId);
        setActiveOverlaysCount(activeOverlaysRef.current.size);
    }, []);

    const unregisterOverlay = useCallback((pageId) => {
        activeOverlaysRef.current.delete(pageId);
        setActiveOverlaysCount(activeOverlaysRef.current.size);
    }, []);

    const updatePrefs = useCallback((updater) => {
        setPrefs(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            savePrefs(next);
            // Sync seenPages to server if they changed
            if (next.seenPages !== prev.seenPages) {
                syncSeenPagesToServer(next.seenPages);
            }
            return next;
        });
    }, []);

    const currentStep = filteredSteps[currentStepIndex] || null;
    const totalSteps = filteredSteps.length;

    const startTour = useCallback((pageId) => {
        if (!pageId || !HELP_DEFINITIONS[pageId]) return;
        // Block tours for background pages when an overlay is active
        const overlays = activeOverlaysRef.current;
        if (overlays.size > 0 && !overlays.has(pageId)) return;
        // Mark as seen immediately — prevents infinite auto-trigger if DOM check fails
        updatePrefs(prev => ({
            ...prev,
            seenPages: { ...prev.seenPages, [pageId]: true }
        }));
        // Pre-filter steps: only keep those with a visible target in the DOM
        const allSteps = HELP_DEFINITIONS[pageId].steps;
        const visible = allSteps.filter(step => {
            const candidates = document.querySelectorAll(step.target);
            return Array.from(candidates).some(e => {
                const r = e.getBoundingClientRect();
                return r.width > 0 && r.height > 0;
            });
        });
        if (visible.length === 0) return;
        setFilteredSteps(visible);
        setCurrentTourPage(pageId);
        setCurrentStepIndex(0);
        setTourActive(true);
    }, [updatePrefs]);

    const stopTour = useCallback(() => {
        setTourActive(false);
        setCurrentTourPage(null);
        setCurrentStepIndex(0);
        setFilteredSteps([]);
    }, []);

    const nextStep = useCallback(() => {
        setCurrentStepIndex(prev => {
            if (prev >= totalSteps - 1) return prev;
            return prev + 1;
        });
    }, [totalSteps]);

    const prevStep = useCallback(() => {
        setCurrentStepIndex(prev => Math.max(0, prev - 1));
    }, []);

    const goToStep = useCallback((index) => {
        setCurrentStepIndex(Math.max(0, Math.min(index, totalSteps - 1)));
    }, [totalSteps]);

    const markPageSeen = useCallback((pageId) => {
        updatePrefs(prev => ({
            ...prev,
            seenPages: { ...prev.seenPages, [pageId]: true }
        }));
    }, [updatePrefs]);

    const resetPageSeen = useCallback((pageId) => {
        updatePrefs(prev => {
            const next = { ...prev.seenPages };
            delete next[pageId];
            return { ...prev, seenPages: next };
        });
    }, [updatePrefs]);

    const resetAllSeen = useCallback(() => {
        updatePrefs(prev => ({ ...prev, seenPages: {} }));
    }, [updatePrefs]);

    const setGlobalEnabled = useCallback((val) => {
        updatePrefs(prev => ({ ...prev, globalEnabled: val }));
    }, [updatePrefs]);

    const setAutoTrigger = useCallback((val) => {
        updatePrefs(prev => ({ ...prev, autoTrigger: val }));
    }, [updatePrefs]);

    const setShowTriggerButton = useCallback((val) => {
        updatePrefs(prev => ({ ...prev, showTriggerButton: val }));
    }, [updatePrefs]);

    const hasSeenPage = useCallback((pageId) => {
        return !!prefs.seenPages[pageId];
    }, [prefs.seenPages]);

    const value = useMemo(() => ({
        helpPrefs: prefs,
        isPrefsSynced,
        isTourActive: tourActive,
        currentTourPage,
        currentStep,
        currentStepIndex,
        totalSteps,
        startTour,
        stopTour,
        nextStep,
        prevStep,
        goToStep,
        markPageSeen,
        resetPageSeen,
        resetAllSeen,
        setGlobalEnabled,
        setAutoTrigger,
        setShowTriggerButton,
        hasSeenPage,
        registerOverlay,
        unregisterOverlay,
        activeHelpPage,
        setActiveHelpPage,
    }), [prefs, isPrefsSynced, tourActive, currentTourPage, currentStep, currentStepIndex, totalSteps,
        startTour, stopTour, nextStep, prevStep, goToStep, markPageSeen,
        resetPageSeen, resetAllSeen, setGlobalEnabled, setAutoTrigger, setShowTriggerButton, hasSeenPage,
        registerOverlay, unregisterOverlay, activeHelpPage]);

    return (
        <HelpContext.Provider value={value}>
            {children}
        </HelpContext.Provider>
    );
};

export const useHelp = () => {
    const context = useContext(HelpContext);
    if (!context) {
        throw new Error('useHelp must be used within a HelpProvider');
    }
    return context;
};
